import React, { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { PinPad } from "@/components/PinPad";
import { Button } from "@/components/ui/button";
import Vault from "@/components/Vault";

export default function VaultLocker({ user, evidence, reload, unlocked, setUnlocked, onSetupPin, matterId, matters, highlight, onHighlightDone }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const unlock = async () => {
    if (pin.length !== 6) { setError("Enter your 6-digit PIN"); return; }
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/vault/verify-pin", { pin });
      localStorage.setItem("ek_vault_token", data.vault_token);
      setUnlocked(true);
      toast.success("Evidence Locker unlocked");
    } catch (e) {
      setError(e.response?.data?.detail || "Incorrect PIN");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  if (!user.has_vault_pin) {
    return (
      <div className="border rounded-lg p-10 text-center max-w-md mx-auto" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="locker-need-pin">
        <Lock className="h-10 w-10 mx-auto mb-4" style={{ color: "var(--ek-warning)" }} strokeWidth={1.3} />
        <h3 className="font-head font-semibold text-xl tracking-tight mb-2">Secure your Evidence Locker</h3>
        <p className="text-sm mb-6" style={{ color: "var(--ek-text-secondary)" }}>
          Set a separate 6-digit Vault PIN. Your evidence stays encrypted and locked until the PIN is entered.
        </p>
        <Button onClick={onSetupPin} data-testid="locker-setup-pin-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
          <KeyRound className="h-4 w-4 mr-2" /> Set Vault PIN
        </Button>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="border rounded-lg p-10 text-center max-w-md mx-auto ek-scanline" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="locker-gate">
        <div className="h-14 w-14 mx-auto mb-4 rounded-lg flex items-center justify-center ek-glow" style={{ background: "var(--ek-page)", border: "1px solid var(--ek-border)" }}>
          <ShieldCheck className="h-7 w-7" style={{ color: "var(--ek-primary)" }} />
        </div>
        <h3 className="font-head font-semibold text-xl tracking-tight mb-1">Evidence Locker is locked</h3>
        <p className="text-sm mb-6" style={{ color: "var(--ek-text-secondary)" }}>Enter your Vault PIN to access encrypted evidence.</p>
        <div className="flex justify-center mb-4"><PinPad value={pin} onChange={setPin} testid="locker-pin-input" autoFocus /></div>
        {error && <p className="text-sm mb-4" style={{ color: "var(--ek-error)" }}>{error}</p>}
        <Button onClick={unlock} disabled={busy} data-testid="locker-unlock-btn" className="rounded-md w-full" style={{ background: "var(--ek-primary)", color: "#fff" }}>
          {busy ? "UNLOCKING..." : "Unlock locker"}
        </Button>
      </motion.div>
    );
  }

  return <Vault evidence={evidence} reload={reload} matterId={matterId} matters={matters} highlight={highlight} onHighlightDone={onHighlightDone} />;
}
